import {useCallback, useContext, useEffect, useState} from 'react';

import uniqueId from 'lodash.uniqueid';
import {
    AUTO_CLOSE_ERROR_MS,
    AUTO_CLOSE_SUCCESS_MS,
    SAVE_CHANGES_SUCCESS_MESSAGE,
} from 'src/constants';
import {BroadcastContext} from 'src/contexts/brodcast-context';
import {parseErrorMessages}
    from 'src/contexts/message-context/message-context-provider';
import {useReadOnlyDataHook} from 'src/hooks/use-read-only-data-hook';
import {AUTH_CALLBACK_EVENTS}
    from 'src/modules/profile/account/tabs/license-and-features/crm-tab/constants';
import {getHubspotCredentialsOauth}
    from 'src/openapi/admin/apis/HubspotControllerApi';
import {useAppDispatch} from 'src/store/hooks';

import {Icons} from '@pb/kit-component-icon';
import {Status, usePushNotification}
    from '@pb/kit-component-push-notification';

const popupWidth = 500;
const popupHeight = 600;
const left = (screen.width - popupWidth) / 2;
const top = (screen.height - popupHeight) / 4;
const SPECS = `width=${popupWidth},height=${popupHeight},toolbar=0,`
    + `scrollbars=1,titlebar=1,menubar=1,resizable=1,directories=1,`
    + `location=1,left=${left},top=${top}`;

type OAuthResult = {success: boolean, error?: string};

type Props = {
    accountId: number,
    onConnectionSuccessful: () => void,
};

export const useGetHubspotAuth = (
    {accountId, onConnectionSuccessful}: Props,
) => {
    const apiDispatch = useAppDispatch();
    const {channel} = useContext(BroadcastContext);
    const {onCreateNote, onCloseNote} = usePushNotification();
    const [oAuthResult, setOAuthResult] =
        useState<OAuthResult | null>(null);

    const getDispatchAction = useCallback(() => apiDispatch(
        getHubspotCredentialsOauth({accountId}),
    ), [accountId, apiDispatch]);

    const {
        isLoading: isOAuthRedirectUrlLoading,
        loadingError: oAuthRedirectUrlLoadingError,
        data: oAuthRedirectCredentials,
    } = useReadOnlyDataHook({
        getDispatchAction,
        initValue: {value: ''},
    });

    useEffect(() => {
        const handleHubspotAuth = (event: MessageEvent<string>) => {
            const messageData = JSON.parse(event.data) as
                {[key: string]: OAuthResult};
            const result = messageData[AUTH_CALLBACK_EVENTS.HUBSPOT];
            if (result !== undefined) {
                setOAuthResult(result);
            }
        };

        if (channel) {
            channel.addEventListener('message', handleHubspotAuth);
        }

        return () => {
            if (channel) {
                channel.removeEventListener(
                    'message', handleHubspotAuth,
                );
            }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (oAuthResult !== null) {
            if (oAuthResult.success) {
                onConnectionSuccessful();
                const noteId = uniqueId('hubspot-oauth-success-');
                onCreateNote({
                    id: noteId,
                    status: Status.SUCCESS,
                    autoHideTimeout: AUTO_CLOSE_SUCCESS_MS,
                    buttonProps: {
                        iconProps: {type: Icons.CLOSE},
                        onClick: () => onCloseNote(noteId),
                    },
                    body: () => SAVE_CHANGES_SUCCESS_MESSAGE,
                });
            } else if (oAuthResult.error) {
                const noteId = uniqueId('hubspot-oauth-error-');
                onCreateNote({
                    id: noteId,
                    status: Status.ERROR,
                    autoHideTimeout: AUTO_CLOSE_ERROR_MS,
                    buttonProps: {
                        iconProps: {type: Icons.CLOSE},
                        onClick: () => onCloseNote(noteId),
                    },
                    body: () => parseErrorMessages(oAuthResult.error!),
                });
            }

            setOAuthResult(null);
        }
    }, [oAuthResult, onConnectionSuccessful, onCreateNote, onCloseNote]);

    const redirectToHubspotOAuth = useCallback(() => {
        if (oAuthRedirectCredentials) {
            window.open(
                oAuthRedirectCredentials.value, '_blank', SPECS,
            );
        }
    }, [oAuthRedirectCredentials]);

    return {
        isOAuthRedirectUrlLoading,
        oAuthRedirectUrlLoadingError,
        redirectToHubspotOAuth,
    };
};
